import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Building2, Edit2, Ban, CheckCircle } from "lucide-react";
import { PageHeader, AppButton, AppBadge, BackButton } from "../../ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/Tabs";
import OverviewTab from "./Tabs/OverviewTab";
import IntegrationsTab from "./Tabs/IntegrationsTab";
import StatsTab from "./Tabs/StatsTab";
import UsersTab from "./Tabs/UsersTab";
import PlanTab from "./Tabs/PlanTab";
import EarningsTab from "./Tabs/EarningsTab";
import DomainTab from "./Tabs/DomainTab";
import InstituteFormModal from "./InstituteFormModal";
import { ConfirmModal } from "../../ui";
import apiClient from "../../../utils/apiClient";
import "./institute-detail.scss";

const InstituteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [institute, setInstitute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'suspend' | 'activate' }

  useEffect(() => {
    fetchInstitute(id);
  }, [id]);

  const fetchInstitute = async (instId) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/sa/institutes/${instId}`);
      // Merge institute + stats + users into one object for tabs
      const data = { ...res.data.institute, stats: res.data.stats, users: res.data.users };
      setInstitute(data);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      name: institute.name,
      email: institute.email,
      phone: institute.phone,
      address: institute.address,
      currency: institute.currency,
      status: institute.status,
      gstNumber: institute.gstNumber,
      subdomain: institute.subdomain || '',
      paymentType: institute.paymentType || 'own_gateway',
    });
    setShowEdit(true);
  };

  const handleUpdate = async (data) => {
    try {
      setFormLoading(true);
      await apiClient.put(`/sa/institutes/${id}`, data);
      setInstitute((prev) => ({ ...prev, ...data }));
      setShowEdit(false);
    } catch {
      // handle error
    } finally {
      setFormLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = institute.status === "suspended" ? "active" : "suspended";
    try {
      await apiClient.patch(`/sa/institutes/${id}/status`, { status: newStatus });
      setInstitute((prev) => ({ ...prev, status: newStatus }));
    } catch {
      // handle error
    }
    setConfirmAction(null);
  };

  if (loading || !institute) {
    return <div className="text-center py-5"><div className="spinner-border" /></div>;
  }

  const isSuspended = institute.status === "suspended";

  return (
    <div className="sa-inst-detail">
      <BackButton onClick={() => navigate("/sa/institutes")} />

      <PageHeader
        title={institute.name}
        description={institute.email}
        icon={Building2}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {isSuspended ? (
              <AppButton variant="outline" size="sm" onClick={() => setConfirmAction({ type: "activate" })}>
                <CheckCircle size={14} /> Reactivate
              </AppButton>
            ) : (
              <AppButton variant="outline" size="sm" onClick={() => setConfirmAction({ type: "suspend" })}>
                <Ban size={14} /> Suspend
              </AppButton>
            )}
            <AppButton variant="default" size="sm" onClick={handleEdit}>
              <Edit2 size={14} /> Edit
            </AppButton>
          </div>
        }
      />

      <div className="sa-inst-detail__badges">
        <AppBadge variant="outline" color={isSuspended ? "rose" : "emerald"}>
          {institute.status}
        </AppBadge>
        <AppBadge variant="secondary">{institute.planId?.name || "No Plan"}</AppBadge>
        <AppBadge variant="secondary">
          {institute.studentCount ?? 0} students
        </AppBadge>
        {institute.paymentType && (
          <AppBadge variant="outline" color={institute.paymentType === "lms_payment" ? "blue" : "emerald"}>
            {institute.paymentType === "lms_payment" ? "LMS Payment" : "Own Gateway"}
          </AppBadge>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="plan">Plan</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="earnings">Earnings</TabsTrigger>
          <TabsTrigger value="domain">Domain</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <OverviewTab institute={institute} />
        </TabsContent>
        <TabsContent value="plan">
          <PlanTab institute={institute} onUpdate={(updated) => setInstitute((prev) => ({ ...prev, ...updated }))} />
        </TabsContent>
        <TabsContent value="stats">
          <StatsTab institute={institute} />
        </TabsContent>
        <TabsContent value="integrations">
          <IntegrationsTab institute={institute} />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab users={institute.users || []} />
        </TabsContent>
        <TabsContent value="earnings">
          <EarningsTab institute={institute} />
        </TabsContent>
        <TabsContent value="domain">
          <DomainTab institute={institute} />
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      <InstituteFormModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        onSubmit={handleUpdate}
        formData={formData}
        setFormData={setFormData}
        loading={formLoading}
        isEdit
      />

      {/* Status Confirm */}
      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={handleStatusToggle}
        title={confirmAction?.type === "suspend" ? "Suspend Institute" : "Reactivate Institute"}
        message={
          confirmAction?.type === "suspend"
            ? "Suspending will block all logins for this institute's users. Are you sure?"
            : "This will re-enable access for all users of this institute."
        }
        confirmText={confirmAction?.type === "suspend" ? "Suspend" : "Reactivate"}
        variant={confirmAction?.type === "suspend" ? "danger" : "success"}
      />
    </div>
  );
};

export default InstituteDetailPage;
